/**
 * Inline SVG icons for the conversation screen, ported 1:1 from
 * claude-design/orborix-speak/project/conversation-screen.jsx +
 * conv-controls.jsx. Typed primitives (no `Object.assign(window, ...)`).
 */

interface IconProps {
  size?: number;
}

export function IconX({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M6 6 L 18 18 M 18 6 L 6 18" />
    </svg>
  );
}

export function IconSliders({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="8" cy="7" r="2.2" />
      <line x1="2" y1="7" x2="6" y2="7" />
      <line x1="10" y1="7" x2="22" y2="7" />
      <circle cx="16" cy="17" r="2.2" />
      <line x1="2" y1="17" x2="14" y2="17" />
      <line x1="18" y1="17" x2="22" y2="17" />
    </svg>
  );
}

/**
 * Icono de entrada a los ajustes de la charla. Un engranaje se lee sin
 * ambigüedad como "ajustes/configuración" — a diferencia del viejo glifo de
 * sliders, que el usuario leía como "filtros" (rediseño v10).
 */
export function IconChatGear({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      {/* Rueda dentada clásica: silueta de dientes (un único path) + núcleo. */}
      <path d="M10.6 2.6 H 13.4 L 13.9 5 A 7.2 7.2 0 0 1 16 6.2 L 18.3 5.3 L 19.7 7.7 L 18 9.3 A 7.2 7.2 0 0 1 18 11.7 V 12.3 A 7.2 7.2 0 0 1 18 14.7 L 19.7 16.3 L 18.3 18.7 L 16 17.8 A 7.2 7.2 0 0 1 13.9 19 L 13.4 21.4 H 10.6 L 10.1 19 A 7.2 7.2 0 0 1 8 17.8 L 5.7 18.7 L 4.3 16.3 L 6 14.7 A 7.2 7.2 0 0 1 6 9.3 L 4.3 7.7 L 5.7 5.3 L 8 6.2 A 7.2 7.2 0 0 1 10.1 5 Z" />
      <circle cx="12" cy="12" r="3.1" />
    </svg>
  );
}

export function IconPlay({ size = 12 }: IconProps) {
  return (
    <svg viewBox="0 0 12 12" width={size} height={size} fill="currentColor">
      <path d="M3 2 L 10 6 L 3 10 Z" />
    </svg>
  );
}

export function IconChevronDown({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9 L 12 15 L 18 9" />
    </svg>
  );
}

export function IconPen({ size = 12 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 4 L 20 10 L 9 21 L 3 21 L 3 15 Z" />
      <path d="M13 5 L 19 11" />
    </svg>
  );
}

export function IconWaveSmall({ size = 12 }: IconProps) {
  return (
    <svg viewBox="0 0 24 14" width={(size * 24) / 14} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M2 7 L 5 7" />
      <path d="M8 2 L 8 12" />
      <path d="M12 4 L 12 10" />
      <path d="M16 1 L 16 13" />
      <path d="M19 7 L 22 7" />
    </svg>
  );
}

export function IconClose({ size = 12 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
      <path d="M6 6 L 18 18 M 18 6 L 6 18" />
    </svg>
  );
}

export function IconCheck({ size = 12 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12 L 10 18 L 20 6" />
    </svg>
  );
}

export function IconBulb({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18 H 15" />
      <path d="M10 21 H 14" />
      <path d="M12 3 a 6 6 0 0 1 4 10 c -1 1 -1 2 -1 3 H 9 c 0 -1 0 -2 -1 -3 a 6 6 0 0 1 4 -10 Z" />
      <path d="M12 8 V 13" />
      <path d="M10 11 H 14" />
    </svg>
  );
}

export function IconBulbFilled({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
      <path d="M12 3 a 6 6 0 0 1 4 10 c -1 1 -1 2 -1 3 H 9 c 0 -1 0 -2 -1 -3 a 6 6 0 0 1 4 -10 Z" />
      <rect x="9" y="17.5" width="6" height="1.8" rx="0.9" />
      <rect x="10" y="20" width="4" height="1.6" rx="0.8" />
    </svg>
  );
}

export function IconSparkleOutline({ size = 16 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
      <path d="M12 3 L 13.4 9.6 L 20 11 L 13.4 12.4 L 12 19 L 10.6 12.4 L 4 11 L 10.6 9.6 Z" />
    </svg>
  );
}

export function IconRefresh({ size = 14 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12 a 8 8 0 0 1 14 -5 L 20 9" />
      <path d="M20 4 V 9 H 15" />
      <path d="M20 12 a 8 8 0 0 1 -14 5 L 4 15" />
      <path d="M4 20 V 15 H 9" />
    </svg>
  );
}

export function IconXSmall({ size = 12 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
      <path d="M6 6 L 18 18 M 18 6 L 6 18" />
    </svg>
  );
}

export function IconPlaySmall({ size = 10 }: IconProps) {
  return (
    <svg viewBox="0 0 12 12" width={size} height={size} fill="currentColor">
      <path d="M3 2 L 10 6 L 3 10 Z" />
    </svg>
  );
}

export function IconMicSolid({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="3" width="6" height="12" rx="3" fill="currentColor" stroke="none" />
      <path d="M5 11 a 7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
    </svg>
  );
}

export function IconMicOff({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="3" width="6" height="12" rx="3" />
      <path d="M5 11 a 7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="4" y1="3" x2="20" y2="21" />
    </svg>
  );
}

export function IconPlayTri({ size = 12 }: IconProps) {
  return (
    <svg viewBox="0 0 12 12" width={size} height={size} fill="currentColor">
      <path d="M3 2 L 10 6 L 3 10 Z" />
    </svg>
  );
}

export function IconChevron({ size = 9 }: IconProps) {
  return (
    <svg viewBox="0 0 12 12" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 4.5 L 6 7.5 L 9 4.5" />
    </svg>
  );
}

export function IconArrowRight({ size = 14 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6 L 15 12 L 9 18" />
    </svg>
  );
}

/** "+" del composer: abre el menú de Actividades. */
export function IconPlus({ size = 20 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <path d="M12 5 V 19 M 5 12 H 19" />
    </svg>
  );
}

// ─── Toggle de voz del coach (header) ─────────────────────────────────────
/** Altavoz con ondas: el coach responde CON voz (TTS activo). */
export function IconSpeaker({ size = 20 }: IconProps) {
  // Cuerpo x:4-12 + ondas hasta x≈20 → el conjunto queda centrado en el
  // viewBox (masa óptica ≈ x=12); ondas simétricas alrededor de y=12.
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5 L 7.5 9 H 4 V 15 H 7.5 L 12 19 Z" />
      <path d="M15.5 9 a 4.2 4.2 0 0 1 0 6" />
      <path d="M18 6.5 a 8 8 0 0 1 0 11" />
    </svg>
  );
}

/** Altavoz tachado: el coach responde SÓLO por texto (voz silenciada). */
export function IconSpeakerOff({ size = 20 }: IconProps) {
  // Sin ondas el cuerpo se centra (x:7-15) y la raya a 45° pasa por (12,12):
  // cruza el parlante por el medio y el glifo entero queda centrado en el
  // botón (el diseño anterior tenía el cuerpo pegado al borde izquierdo y la
  // raya cruzando el vacío de la derecha).
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 5 L 10.5 9 H 7 V 15 H 10.5 L 15 19 Z" />
      <path d="M6.5 6.5 L 17.5 17.5" />
    </svg>
  );
}

/** Velocímetro para el SpeedQuickControl. */
export function IconSpeed({ size = 14 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 14 a 8 8 0 0 1 16 0" />
      <path d="M12 14 L 15.5 8.5" />
      <circle cx="12" cy="14" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

// ─── Bloque C/D: word popover + traducción por burbuja ────────────────────
export function IconLanguageSmall({ size = 12 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5 H 12" />
      <path d="M7 3 V 5" />
      <path d="M5 5 C 5 11, 11 13, 13 13" />
      <path d="M11 5 C 11 9, 7 13, 4 13" />
      <path d="M13 21 L 17.5 11 L 22 21" />
      <path d="M14.5 18 H 20.5" />
    </svg>
  );
}

export function IconBookmarkSmall({ size = 12 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 4 H 18 V 21 L 12 17 L 6 21 Z" />
    </svg>
  );
}

export function IconStarSmall({ size = 12 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
      <path d="M12 3 L 14.4 9.4 L 21 10 L 16 14.4 L 17.6 21 L 12 17.4 L 6.4 21 L 8 14.4 L 3 10 L 9.6 9.4 Z" />
    </svg>
  );
}

// ─── Bloque E: chevrons del CoachPicker ───────────────────────────────────
export function IconChevSmall({ size = 18, dir = 'left' }: IconProps & { dir?: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: dir === 'right' ? 'rotate(180deg)' : 'none' }}
    >
      <path d="M14 6 L 8 12 L 14 18" />
    </svg>
  );
}
